import React from "react";
import { getSurfaceFill, getToothShape, INTERACTION_COLORS, SURFACE_LABELS, ODONTOGRAM_COLORS, estimatePathBounds } from "./utils/toothChartUtils";
import { getToothViewData, isLeftSide } from "./utils/toothPathData";
import { getProcedurePathsForView, STROKE_ONLY_PROCEDURES } from "./utils/procedureOverlayPaths";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const INTERACTIVE_OVERLAY_STYLE = {
  cursor: "pointer",
  pointerEvents: "all"
};
const FACIAL_SURFACES = {
  molar: ["M", "D", "O", "B", "L"],
  premolar: ["M", "D", "O", "B", "L"],
  canine: ["M", "D", "I", "B", "L"],
  incisor: ["M", "D", "I", "B", "L"]
};
const NORMALIZED_BASE_FILL = "#ede5d4";
const NORMALIZED_BASE_STROKE = "#a89060";
function getViewViewport(viewData) {
  const raw = viewData.bounds ? {
    minX: viewData.bounds.minX,
    maxX: viewData.bounds.maxX,
    minY: viewData.bounds.minY,
    maxY: viewData.bounds.maxY
  } : estimatePathBounds(viewData.outline);
  const width = Math.max(raw.maxX - raw.minX, 1);
  const height = Math.max(raw.maxY - raw.minY, 1);
  const padX = Math.max(0.8, width * 0.06);
  const padY = Math.max(0.8, height * 0.06);
  const minX = raw.minX - padX;
  const maxX = raw.maxX + padX;
  const minY = raw.minY - padY;
  const maxY = raw.maxY + padY;
  const vpWidth = Math.max(maxX - minX, 1);
  const vpHeight = Math.max(maxY - minY, 1);
  const axisX = minX + vpWidth / 2;
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: vpWidth,
    height: vpHeight,
    mirrorTranslateX: axisX * 2
  };
}
function canonicalSurface(surface, mirror) {
  let canonical = surface;
  if (surface === "F") canonical = "B";
  if (surface === "I") canonical = "O";
  if (surface === "P") canonical = "L";
  if (mirror) {
    if (canonical === "M") return "D";
    if (canonical === "D") return "M";
  }
  return canonical;
}
function buildSyntheticOcclusalPath(viewData, shape, view, upper) {
  const b = viewData.bounds ? {
    minX: viewData.bounds.minX,
    maxX: viewData.bounds.maxX,
    minY: viewData.bounds.minY,
    maxY: viewData.bounds.maxY,
    cx: (viewData.bounds.minX + viewData.bounds.maxX) / 2,
    cy: (viewData.bounds.minY + viewData.bounds.maxY) / 2
  } : estimatePathBounds(viewData.outline);
  const widthFactor = {
    molar: 0.62,
    premolar: 0.56,
    canine: 0.46,
    incisor: 0.54
  };
  const rx = Math.max(2.8, (b.maxX - b.minX) * widthFactor[shape] * 0.5);
  const ry = Math.max(1.3, (b.maxY - b.minY) * (shape === "molar" || shape === "premolar" ? 0.07 : 0.055));

  // Place "O/I" near the inter-view boundary:
  // upper: buccal(bottom), lingual(top)
  // lower: lingual(bottom), buccal(top)
  const towardBottom = upper && view === "buccal" || !upper && view === "lingual";
  // Keep this close to edge so it reads as an incisal/occlusal strip.
  const cy = towardBottom ? b.maxY - ry * 0.45 : b.minY + ry * 0.45;
  const cx = b.cx;
  return `M${(cx - rx).toFixed(3)},${cy.toFixed(3)} A${rx.toFixed(3)},${ry.toFixed(3)} 0 1 0 ${(cx + rx).toFixed(3)},${cy.toFixed(3)} A${rx.toFixed(3)},${ry.toFixed(3)} 0 1 0 ${(cx - rx).toFixed(3)},${cy.toFixed(3)} Z`;
}
function getSurfacePaths(viewData, surface, mirror, view, upper, shape) {
  const key = canonicalSurface(surface, mirror);
  const paths = viewData.surfacePaths?.[key];
  if (Array.isArray(paths) && paths.length) return paths;

  // Some extracted views miss explicit "O/I" geometry. Synthesize a compact patch
  // near the inter-view boundary so incisal/occlusal selection remains visible.
  if (key === "O") {
    return [buildSyntheticOcclusalPath(viewData, shape, view, upper)];
  }
  return [];
}
function ProcedureOverlayGroup({
  type,
  color,
  shape,
  upper,
  dashed,
  toothNumber,
  view
}) {
  const paths = getProcedurePathsForView(type, shape, upper, toothNumber, view);
  if (!paths.length) return null;
  const isStrokeOnly = STROKE_ONLY_PROCEDURES.has(type);
  const dashArray = dashed ? "3 2" : undefined;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: paths.map((d, i) => /*#__PURE__*/_jsx("path", {
      d: d,
      fill: isStrokeOnly ? "none" : color,
      fillOpacity: isStrokeOnly ? undefined : 0.5,
      stroke: color,
      strokeWidth: isStrokeOnly ? 2.2 : 1.2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeDasharray: dashArray,
      opacity: dashed ? 0.85 : 1
    }, `${type}-${i}`))
  });
}
const CurveToothSVG = ({
  tooth,
  selected,
  onSelect,
  view,
  upper = false,
  width: svgWidth = 40,
  height: svgHeight = 80,
  selectedSurfaces,
  onSurfaceToggle,
  hoveredSurface,
  onSurfaceHover,
  enhancedState,
  layerVisibility
}) => {
  const clipIdSeed = React.useId().replace(/:/g, "");
  const shape = getToothShape(tooth.toothNumber);
  const viewData = getToothViewData(tooth.toothNumber, view);
  const interactive = !!onSurfaceToggle;
  const mirror = isLeftSide(tooth.toothNumber);
  const surfaceClipPathId = `tooth-surface-clip-${tooth.toothNumber}-${view}-${clipIdSeed}`;
  const baseStatus = enhancedState?.baseStatus ?? (tooth.condition === "missing" ? "missing" : "permanent");
  if (!viewData) {
    return /*#__PURE__*/_jsx("svg", {
      viewBox: "0 0 50 100",
      width: svgWidth,
      height: svgHeight,
      className: "cursor-pointer",
      onClick: onSelect,
      "data-tooth-number": tooth.toothNumber,
      "data-tooth-view": view,
      children: /*#__PURE__*/_jsx("rect", {
        x: 10,
        y: 10,
        width: 30,
        height: 80,
        rx: 6,
        fill: "#f3f4f6",
        stroke: "#d1d5db"
      })
    });
  }
  const viewport = getViewViewport(viewData);
  const viewBox = `${viewport.minX} ${viewport.minY} ${viewport.width} ${viewport.height}`;
  const mirrorTransform = mirror ? `translate(${viewport.mirrorTranslateX},0) scale(-1,1)` : undefined;
  const outlineBounds = viewData.bounds ? {
    minX: viewData.bounds.minX,
    maxX: viewData.bounds.maxX,
    minY: viewData.bounds.minY,
    maxY: viewData.bounds.maxY
  } : estimatePathBounds(viewData.outline);
  const renderPathLayer = (d, key, props) => /*#__PURE__*/_jsx("path", {
    d: d,
    ...props
  }, key);

  // Missing tooth silhouette
  if (baseStatus === "missing") {
    return /*#__PURE__*/_jsx("svg", {
      viewBox: viewBox,
      width: svgWidth,
      height: svgHeight,
      className: "cursor-pointer",
      onClick: onSelect,
      "data-tooth-number": tooth.toothNumber,
      "data-tooth-view": view,
      children: /*#__PURE__*/_jsx("g", {
        transform: mirrorTransform,
        children: /*#__PURE__*/_jsx("path", {
          d: viewData.outline,
          fill: ODONTOGRAM_COLORS.missingSilhouette,
          stroke: "#9ca3af",
          strokeWidth: 1,
          strokeDasharray: "4 3",
          opacity: 0.72
        })
      })
    });
  }

  // Unerupted tooth
  if (baseStatus === "unerupted") {
    return /*#__PURE__*/_jsx("svg", {
      viewBox: viewBox,
      width: svgWidth,
      height: svgHeight,
      className: "cursor-pointer",
      onClick: onSelect,
      "data-tooth-number": tooth.toothNumber,
      "data-tooth-view": view,
      children: /*#__PURE__*/_jsx("g", {
        transform: mirrorTransform,
        children: /*#__PURE__*/_jsx("path", {
          d: viewData.outline,
          fill: ODONTOGRAM_COLORS.uneruptedFill,
          stroke: ODONTOGRAM_COLORS.uneruptedStroke,
          strokeWidth: 1,
          opacity: 0.82
        })
      })
    });
  }

  // Extracted marker on this view
  if (baseStatus === "extracted") {
    return /*#__PURE__*/_jsx("svg", {
      viewBox: viewBox,
      width: svgWidth,
      height: svgHeight,
      className: "cursor-pointer",
      onClick: onSelect,
      "data-tooth-number": tooth.toothNumber,
      "data-tooth-view": view,
      children: /*#__PURE__*/_jsxs("g", {
        transform: mirrorTransform,
        children: [/*#__PURE__*/_jsx("path", {
          d: viewData.outline,
          fill: "none",
          stroke: "#9ca3af",
          strokeWidth: 1,
          strokeDasharray: "3 2",
          opacity: 0.7
        }), /*#__PURE__*/_jsx("line", {
          x1: outlineBounds.minX + 2,
          y1: outlineBounds.minY + 2,
          x2: outlineBounds.maxX - 2,
          y2: outlineBounds.maxY - 2,
          stroke: "#9ca3af",
          strokeWidth: 1.1,
          opacity: 0.65
        }), /*#__PURE__*/_jsx("line", {
          x1: outlineBounds.maxX - 2,
          y1: outlineBounds.minY + 2,
          x2: outlineBounds.minX + 2,
          y2: outlineBounds.maxY - 2,
          stroke: "#9ca3af",
          strokeWidth: 1.1,
          opacity: 0.65
        })]
      })
    });
  }

  // Legacy per-surface fills from tooth.surfaces when enhanced state isn't provided.
  const legacySurfaceOverlays = !enhancedState ? (tooth.surfaces ?? []).flatMap(s => {
    const fill = getSurfaceFill(s);
    if (fill === "transparent") return [];
    return getSurfacePaths(viewData, s.surface, mirror, view, upper, shape).map((d, idx) => renderPathLayer(d, `legacy-${s.surface}-${idx}`, {
      fill,
      opacity: 0.65
    }));
  }) : [];
  const enhancedCompletedSurfaceOverlays = enhancedState ? enhancedState.surfaces.completed.flatMap(s => getSurfacePaths(viewData, s.surface, mirror, view, upper, shape).map((d, idx) => renderPathLayer(d, `enh-comp-${s.surface}-${idx}`, {
    fill: s.color,
    opacity: 0.68
  }))) : [];
  const enhancedPlannedSurfaceOverlays = enhancedState ? enhancedState.surfaces.planned.flatMap(s => getSurfacePaths(viewData, s.surface, mirror, view, upper, shape).map((d, idx) => renderPathLayer(d, `enh-plan-${s.surface}-${idx}`, {
    fill: s.color,
    fillOpacity: 0.25,
    stroke: s.color,
    strokeWidth: 1.1,
    strokeDasharray: "3 2"
  }))) : [];
  const cariesOverlays = enhancedState && layerVisibility?.caries !== false ? enhancedState.caries.flatMap(s => getSurfacePaths(viewData, s, mirror, view, upper, shape).map((d, idx) => renderPathLayer(d, `caries-${s}-${idx}`, {
    fill: "none",
    stroke: ODONTOGRAM_COLORS.cariesOutline,
    strokeWidth: 1.25,
    opacity: 0.9
  }))) : [];
  const interactiveSurfaceOverlays = interactive ? FACIAL_SURFACES[shape].flatMap(surf => {
    const paths = getSurfacePaths(viewData, surf, mirror, view, upper, shape);
    if (!paths.length) return [];
    const isSelected = selectedSurfaces?.includes(surf) ?? false;
    const isHovered = hoveredSurface === surf;
    let fill = "transparent";
    let stroke = "transparent";
    let strokeWidth = 0;
    let opacity = 0;
    if (isSelected) {
      fill = INTERACTION_COLORS.selectedFill;
      stroke = INTERACTION_COLORS.selectedStroke;
      strokeWidth = 1.2;
      opacity = 0.58;
    } else if (isHovered) {
      fill = INTERACTION_COLORS.hoverFill;
      stroke = INTERACTION_COLORS.hoverStroke;
      strokeWidth = 0.9;
      opacity = 0.4;
    }
    return paths.map((d, idx) => renderPathLayer(d, `interact-${surf}-${idx}`, {
      fill,
      stroke,
      strokeWidth,
      opacity,
      style: INTERACTIVE_OVERLAY_STYLE,
      role: "button",
      "aria-label": `${SURFACE_LABELS[surf]} surface`,
      onClick: e => {
        e.stopPropagation();
        onSurfaceToggle?.(surf);
      },
      onMouseEnter: () => onSurfaceHover?.(surf),
      onMouseLeave: () => onSurfaceHover?.(null)
    }));
  }) : [];
  const completedProcedures = enhancedState && layerVisibility?.completedProcedures !== false ? enhancedState.procedures.completed.map(proc => /*#__PURE__*/_jsx(ProcedureOverlayGroup, {
    type: proc.type,
    color: proc.color ?? ODONTOGRAM_COLORS.completed,
    shape: shape,
    upper: upper,
    toothNumber: tooth.toothNumber,
    view: view
  }, `c-${proc.type}`)) : null;
  const plannedProcedures = enhancedState && layerVisibility?.plannedProcedures !== false ? enhancedState.procedures.planned.map(proc => /*#__PURE__*/_jsx(ProcedureOverlayGroup, {
    type: proc.type,
    color: proc.color ?? ODONTOGRAM_COLORS.planned,
    shape: shape,
    upper: upper,
    toothNumber: tooth.toothNumber,
    view: view,
    dashed: true
  }, `p-${proc.type}`)) : null;
  return /*#__PURE__*/_jsxs("svg", {
    viewBox: viewBox,
    width: svgWidth,
    height: svgHeight,
    className: "cursor-pointer",
    onClick: onSelect,
    "data-tooth-number": tooth.toothNumber,
    "data-tooth-view": view,
    children: [/*#__PURE__*/_jsx("defs", {
      children: /*#__PURE__*/_jsx("clipPath", {
        id: surfaceClipPathId,
        clipPathUnits: "userSpaceOnUse",
        children: viewData.layers.map((layer, i) => /*#__PURE__*/_jsx("path", {
          d: layer.d
        }, `clip-${i}`))
      })
    }), /*#__PURE__*/_jsxs("g", {
      transform: mirrorTransform,
      children: [selected && /*#__PURE__*/_jsx("path", {
        d: viewData.outline,
        fill: "#93c5fd",
        opacity: 0.2
      }), viewData.layers.map((layer, i) => /*#__PURE__*/_jsx("path", {
        d: layer.d,
        fill: layer.fill ?? NORMALIZED_BASE_FILL,
        stroke: layer.stroke,
        strokeWidth: typeof layer.strokeWidth === "number" ? layer.strokeWidth : undefined,
        strokeLinecap: layer.strokeLinecap,
        strokeLinejoin: layer.strokeLinejoin,
        opacity: typeof layer.opacity === "number" ? layer.opacity : undefined,
        fillRule: layer.fillRule,
        pointerEvents: "none"
      }, `layer-${i}`)), !enhancedState && tooth.condition === "crown" && /*#__PURE__*/_jsx("path", {
        d: viewData.outline,
        fill: "#dbeafe",
        opacity: 0.35,
        pointerEvents: "none"
      }), completedProcedures, plannedProcedures, /*#__PURE__*/_jsxs("g", {
        clipPath: `url(#${surfaceClipPathId})`,
        children: [legacySurfaceOverlays, enhancedCompletedSurfaceOverlays, enhancedPlannedSurfaceOverlays, cariesOverlays, interactiveSurfaceOverlays]
      }), /*#__PURE__*/_jsx("path", {
        d: viewData.outline,
        fill: "none",
        stroke: selected ? "#2563eb" : NORMALIZED_BASE_STROKE,
        strokeWidth: selected ? 1.5 : 0.8,
        opacity: selected ? 0.95 : 0.6,
        pointerEvents: "none"
      })]
    })]
  });
};
export default /*#__PURE__*/React.memo(CurveToothSVG);